import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Copy, Share2, Check, UserPlus } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Dashboard tile — lets the user grab their personal invite link, copy it,
 * or share it via the native Web Share sheet (WhatsApp, SMS, Slack, etc).
 * Both parties get +500 credits when the invited friend signs in.
 */
export default function InviteCard() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/invites/mine`, {
          withCredentials: true,
        });
        if (!cancelled) setData(r.data);
      } catch (_e) {
        // Silent — the card just won't render useful state.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const link = data
    ? `${window.location.origin}/invite/${data.code}`
    : "";

  const handleCopy = useCallback(async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Invite link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (_e) {
      toast.error("Copy failed — select the link manually");
    }
  }, [link]);

  const handleShare = useCallback(async () => {
    if (!link) return;
    const text = `Pull the Nitro Auto Heist with me — retro arcade racing, bosses, FBI chases. We both get +500 credits when you sign in.`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Nitro Auto Heist",
          text,
          url: link,
        });
      } catch (_e) { /* user cancelled */ }
    } else {
      handleCopy();
    }
  }, [link, handleCopy]);

  if (!data) return null;

  return (
    <div
      className="surface-solid p-6 border-2 border-transparent hover:border-[#FFCC00]/50 transition-colors"
      data-testid="dashboard-invite-card"
    >
      <div className="flex items-start gap-5">
        <div className="w-14 h-14 flex items-center justify-center border-2 border-[#FFCC00] bg-[#FFCC00]/10 text-[#FFCC00]">
          <UserPlus className="w-7 h-7" strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="label-kicker">Invite a Friend</div>
          <div className="font-heading text-2xl mt-1">Share the heist.</div>
          <p className="text-xs text-white/50 mt-1">
            Both of you get{" "}
            <span className="text-[#FFCC00] font-mono">+{data.reward_per_invite}</span>{" "}
            credits when they sign in.
            {data.invites_accepted > 0 && (
              <>
                {" · "}
                <span className="text-white/80">
                  <span className="text-[#FFCC00] font-mono">{data.invites_accepted}</span>{" "}
                  already joined
                </span>
              </>
            )}
          </p>

          <div className="mt-4 flex items-center gap-2">
            <div
              className="flex-1 min-w-0 font-mono text-xs bg-black/40 border border-white/10 px-3 py-2.5 truncate"
              data-testid="invite-link-input"
              title={link}
            >
              {link}
            </div>
            <button
              onClick={handleCopy}
              className="px-3 py-2.5 border border-white/15 hover:border-[#FFCC00] hover:text-[#FFCC00] transition-colors"
              data-testid="invite-copy-btn"
              aria-label="Copy invite link"
            >
              {copied ? <Check className="w-4 h-4 text-[#34C759]" /> : <Copy className="w-4 h-4" />}
            </button>
            <button
              onClick={handleShare}
              className="px-3 py-2.5 border border-[#FFCC00] bg-[#FFCC00] text-black hover:bg-[#FFD633] transition-colors font-heading text-sm uppercase tracking-wider flex items-center gap-1.5"
              data-testid="invite-share-btn"
            >
              <Share2 className="w-3.5 h-3.5" /> Share
            </button>
          </div>
          <div className="mt-3 text-[10px] font-mono tracking-[0.3em] uppercase text-white/30">
            YOUR CODE · {data.code}
          </div>
        </div>
      </div>
    </div>
  );
}
